/**
 * System Audit Service
 *
 * Persists audit runs, dismissed findings, and applied fixes to Supabase.
 * Fails gracefully — all errors are logged but never thrown,
 * so the audit works in-memory even if the DB tables don't exist yet.
 *
 * Required tables:
 *   system_audit_runs    — one row per audit execution with summary counts
 *   system_audit_actions — one row per dismissed or applied finding action
 */

import { supabase } from "@/lib/supabase";
import type { IntegrityReport, IntegrityFinding } from "@/data/systemIntegrity";
import {
  toDbAuditRun,
  toDbAuditAction,
  toUiAuditRun,
  toUiAuditAction,
} from "@/mappers/system-audit.mapper";

// ── DB row shapes — match the Supabase table columns ─────────────────────────

export interface SystemAuditRunRow {
  id: string;
  ran_at: string;
  total: number;
  critical: number;
  warning: number;
  info: number;
  checks_run: number;
  passed: number;
  created_at: string;
}

export interface SystemAuditActionRow {
  id: string;
  finding_id: string;
  action_type: "applied" | "dismissed";
  performed_by: string | null;
  note: string | null;
  created_at: string;
}

// ── UI-facing types ───────────────────────────────────────────────────────────

export interface AuditRunSummary {
  id: string;
  ranAt: string;
  total: number;
  critical: number;
  warning: number;
  info: number;
  checksRun: number;
  passed: number;
}

export interface AuditAction {
  id: string;
  findingId: string;
  actionType: "applied" | "dismissed";
  performedBy: string | null;
  note: string | null;
  createdAt: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const SystemAuditService = {
  /**
   * Persist a completed audit run.
   * Returns the inserted run's ID, or null on failure.
   */
  async saveRun(report: IntegrityReport): Promise<string | null> {
    try {
      const row = toDbAuditRun(report);
      const { data, error } = await supabase
        .from("system_audit_runs")
        .insert(row)
        .select("id")
        .single();

      if (error) {
        console.warn("[SystemAuditService.saveRun] DB error:", error.message);
        return null;
      }
      return (data as { id: string }).id;
    } catch (err) {
      console.warn("[SystemAuditService.saveRun] Exception:", err);
      return null;
    }
  },

  /**
   * Fetch the N most recent audit run summaries (newest first).
   * Used to populate audit history and trend data.
   */
  async getRecentRuns(limit = 10): Promise<AuditRunSummary[]> {
    try {
      const { data, error } = await supabase
        .from("system_audit_runs")
        .select("*")
        .order("ran_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.warn("[SystemAuditService.getRecentRuns] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as SystemAuditRunRow[]).map(toUiAuditRun);
    } catch (err) {
      console.warn("[SystemAuditService.getRecentRuns] Exception:", err);
      return [];
    }
  },

  /**
   * Record a dismissed or applied action for a finding.
   * These are persisted so the UI can restore state across page refreshes.
   */
  async recordAction(
    finding: IntegrityFinding,
    actionType: "applied" | "dismissed",
    performedBy?: string,
    note?: string,
  ): Promise<void> {
    try {
      const row = toDbAuditAction(finding, actionType, performedBy, note);
      const { error } = await supabase
        .from("system_audit_actions")
        .insert(row);

      if (error) {
        console.warn("[SystemAuditService.recordAction] DB error:", error.message);
      }
    } catch (err) {
      console.warn("[SystemAuditService.recordAction] Exception:", err);
    }
  },

  /**
   * Fetch all finding IDs that have been dismissed.
   * Called on mount to restore dismissed state across refreshes.
   */
  async getDismissedIds(): Promise<Set<string>> {
    try {
      const { data, error } = await supabase
        .from("system_audit_actions")
        .select("finding_id")
        .eq("action_type", "dismissed");

      if (error) {
        console.warn("[SystemAuditService.getDismissedIds] DB error:", error.message);
        return new Set();
      }
      return new Set(((data ?? []) as { finding_id: string }[]).map(r => r.finding_id));
    } catch (err) {
      console.warn("[SystemAuditService.getDismissedIds] Exception:", err);
      return new Set();
    }
  },

  /**
   * Fetch all finding IDs that have been applied.
   * Called on mount to restore applied state across refreshes.
   */
  async getAppliedIds(): Promise<Set<string>> {
    try {
      const { data, error } = await supabase
        .from("system_audit_actions")
        .select("finding_id")
        .eq("action_type", "applied");

      if (error) {
        console.warn("[SystemAuditService.getAppliedIds] DB error:", error.message);
        return new Set();
      }
      return new Set(((data ?? []) as { finding_id: string }[]).map(r => r.finding_id));
    } catch (err) {
      console.warn("[SystemAuditService.getAppliedIds] Exception:", err);
      return new Set();
    }
  },

  /**
   * Fetch the full action log for audit trail display.
   * Ordered newest first.
   */
  async getActionLog(limit = 50): Promise<AuditAction[]> {
    try {
      const { data, error } = await supabase
        .from("system_audit_actions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.warn("[SystemAuditService.getActionLog] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as SystemAuditActionRow[]).map(toUiAuditAction);
    } catch (err) {
      console.warn("[SystemAuditService.getActionLog] Exception:", err);
      return [];
    }
  },
};
